/**
 * Layout mode selection — auto picks horizontal vs vertical by playable area size.
 */
import { computeResponsiveBoard } from "./responsive-board.js";
import { computeVerticalBoard } from "./vertical-board.js";

import {
  LAYOUT_MODE_STORAGE_KEY,
  StorageSlot,
  readStorageRecord,
  writeStorageRecord,
} from "../core/storage.js";

export { LAYOUT_MODE_STORAGE_KEY };

/** Default layout preference when unset or invalid. */
export const DEFAULT_LAYOUT_PREFERENCE = "auto";

/** Allowed stored / UI preference values. */
export const LAYOUT_PREFERENCES = ["auto", "vertical", "horizontal"];

/** Resolved runtime layout modes (no `auto`). */
export const LAYOUT_MODES = ["horizontal", "vertical"];

/**
 * @param {unknown} value
 * @returns {'auto' | 'vertical' | 'horizontal'}
 */
export function normalizeLayoutPreference(value) {
  if (value === "vertical" || value === "horizontal" || value === "auto") {
    return value;
  }
  return DEFAULT_LAYOUT_PREFERENCE;
}

/**
 * Read layout preference from localStorage when available.
 * @param {Storage | null | undefined} [storage]
 * @returns {'auto' | 'vertical' | 'horizontal'}
 */
export function readLayoutModePreference(storage) {
  var store = storage;
  if (store == null && typeof localStorage !== "undefined") {
    store = localStorage;
  }
  if (!store || typeof store.getItem !== "function") {
    return DEFAULT_LAYOUT_PREFERENCE;
  }
  var stored = readStorageRecord(store, StorageSlot.LAYOUT_MODE);
  return normalizeLayoutPreference(typeof stored === "string" ? stored : null);
}

/**
 * @param {'auto' | 'vertical' | 'horizontal'} preference
 * @param {Storage | null | undefined} [storage]
 */
export function writeLayoutModePreference(preference, storage) {
  var store = storage;
  if (store == null && typeof localStorage !== "undefined") {
    store = localStorage;
  }
  if (!store) return normalizeLayoutPreference(preference);
  var pref = normalizeLayoutPreference(preference);
  writeStorageRecord(store, StorageSlot.LAYOUT_MODE, pref);
  return pref;
}

/**
 * @param {unknown} [preference] explicit value or localStorage when omitted
 * @param {Storage | null | undefined} [storage]
 * @returns {'auto' | 'vertical' | 'horizontal'}
 */
export function resolveLayoutMode(preference, storage) {
  if (preference === undefined || preference === null) {
    return readLayoutModePreference(storage);
  }
  return normalizeLayoutPreference(preference);
}

function playAreaPixels(layout) {
  var play = layout && layout.playArea;
  if (!play) return 0;
  return Math.max(0, Math.floor(play.width)) * Math.max(0, Math.floor(play.height));
}

/**
 * Compare horizontal vs vertical play areas; pick the larger width × height.
 * Ties favour horizontal (existing default).
 * @param {{ width: number, height: number }} viewport
 * @param {Parameters<typeof computeResponsiveBoard>[1]} [opts]
 * @returns {'horizontal' | 'vertical'}
 */
export function pickLayoutMode(viewport, opts) {
  var horizontal = computeResponsiveBoard(viewport, opts);
  var vertical = computeVerticalBoard(viewport, opts);
  var horizontalArea = playAreaPixels(horizontal);
  var verticalArea = playAreaPixels(vertical);
  if (verticalArea > horizontalArea) {
    return "vertical";
  }
  return "horizontal";
}

/**
 * Effective layout mode after applying a stored preference.
 * @param {{ width: number, height: number }} viewport
 * @param {unknown} [preference]
 * @param {Parameters<typeof computeResponsiveBoard>[1]} [opts]
 * @param {Storage | null | undefined} [storage]
 * @returns {'horizontal' | 'vertical'}
 */
export function resolveEffectiveLayoutMode(viewport, preference, opts, storage) {
  var pref = resolveLayoutMode(preference, storage);
  if (pref === "vertical" || pref === "horizontal") {
    return pref;
  }
  return pickLayoutMode(viewport, opts);
}